/**
 * ly-ticker.js
 * Pause/resume toggle for any scrolling ticker that has:
 *   [data-lyt-root]        — the ticker root element (carries data-paused state)
 *   [data-lyt-toggle]      — the pause/resume button inside the root
 *   [data-lyt-marquee-row] — optional; when present, clones .lyt__track nodes until
 *                            the row is wide enough to loop without a visible snap
 *                            when content is shorter than the viewport.
 *
 * Safe to include multiple times per page; each root is only wired once.
 * Theme editor: listens for shopify:section:load so marquees re-clone after refresh.
 */
(function () {
  'use strict';

  const MAX_TRACK_CLONE_ROUNDS = 10;
  const MAX_TRACKS = 32;

  function ensureSeamlessMarquee(root) {
    const row = root.querySelector('[data-lyt-marquee-row]');
    const viewport = root.querySelector('.lyt__viewport');
    if (!row || !viewport || row.children.length === 0) return;

    const vw = viewport.clientWidth;
    let rounds = 0;
    while (
      rounds < MAX_TRACK_CLONE_ROUNDS &&
      row.children.length < MAX_TRACKS &&
      row.scrollWidth < vw * 2
    ) {
      rounds++;
      const snapshot = Array.prototype.slice.call(row.children);
      for (let i = 0; i < snapshot.length; i++) {
        row.appendChild(snapshot[i].cloneNode(true));
      }
      row.style.setProperty('--lyt-segments', String(row.children.length));
    }
  }

  function initTicker(root) {
    if (root.dataset.lytInit) return;
    root.dataset.lytInit = 'true';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ensureSeamlessMarquee(root);
      });
    });

    const toggle = root.querySelector('[data-lyt-toggle]');
    if (!toggle) return;

    const labelPause = toggle.dataset.lytLabelPause || 'Pause announcements';
    const labelResume = toggle.dataset.lytLabelResume || 'Resume announcements';

    toggle.addEventListener(
      'click',
      () => {
        const isPaused = root.getAttribute('data-paused') === 'true';
        const nextPaused = !isPaused;
        root.setAttribute('data-paused', String(nextPaused));
        toggle.setAttribute('aria-pressed', String(nextPaused));
        toggle.textContent = nextPaused ? labelResume : labelPause;
      },
      { passive: true }
    );
  }

  function initAll() {
    document.querySelectorAll('[data-lyt-root]').forEach(initTicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (ev) => {
    const target = ev.target;
    if (!target || typeof target.querySelectorAll !== 'function') return;
    target.querySelectorAll('[data-lyt-root]').forEach(initTicker);
  });
})();
